import React, { useState, useContext } from "react";
import { Lock, FileKey, ShieldCheck, AlertCircle, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { LoginContext } from "../LoginContext";
import { useTranslation } from "react-i18next";
import apiService from "../backend/api";

export default function PageChangePassword() {
  const { loginInfo } = useContext(LoginContext);
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear message when user starts typing again
    if (message.text) setMessage({ type: "", text: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.newPassword !== formData.confirmPassword) {
      setMessage({ type: "error", text: t("profile.passwords_dont_match", "Passwörter stimmen nicht überein") });
      return;
    }

    if (formData.newPassword.length < 8) {
      setMessage({ type: "error", text: t("profile.password_too_short", "Das Passwort muss mindestens 8 Zeichen lang sein") });
      return;
    }

    if (formData.newPassword === formData.currentPassword) {
      setMessage({ type: "error", text: t("profile.password_same_as_current", "Das neue Passwort darf nicht identisch mit dem aktuellen sein") });
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.changePassword(
        formData.currentPassword,
        formData.newPassword,
        formData.confirmPassword
      );

      setMessage({ type: "success", text: t("profile.password_changed_success", "Passwort erfolgreich geändert") });
      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      console.error("Change password error:", error);
      setMessage({
        type: "error",
        text: error.message || t("profile.password_change_failed", "Fehler beim Ändern des Passworts")
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      {/* Compact Header */}
      <div className="flex items-center gap-3 sm:gap-4 mb-6">
        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#0f7699]/10 dark:bg-[#4fc3f7]/10 rounded-xl sm:rounded-2xl flex items-center justify-center text-[#0f7699] dark:text-[#4fc3f7]">
          <Lock size={20} className="sm:hidden" /><Lock size={24} className="hidden sm:block" />
        </div>
        <div>
          <h1 className="text-lg sm:text-2xl font-black text-[#0f7699] dark:text-[#4fc3f7] tracking-tight">
            {t("profile.passwort_andern", "Passwort ändern")}
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium mt-0.5 hidden sm:block">
            {t("profile.sicherheit_verwalten", "Verwalten Sie Ihre Kontosicherheit")}
          </p>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-2xl sm:rounded-[2rem] shadow-sm border border-slate-200 dark:border-slate-700 p-5 sm:p-8 md:p-10">
          <form onSubmit={handleSubmit} className="space-y-6">
            {message.text && (
              <div className={`p-4 rounded-2xl flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-300 ${
                message.type === "success" ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-100 dark:border-emerald-800" : "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-100 dark:border-red-800"
              }`}>
                {message.type === "success" ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
                <p className="text-sm font-bold">{message.text}</p>
              </div>
            )}

            <div className="space-y-2">
              <label htmlFor="currentPassword" className="text-[10px] font-black uppercase tracking-widest text-[#0f7699] dark:text-[#4fc3f7] opacity-70 ml-1">
                {t("profile.aktuelles_passwort", "Aktuelles Passwort")}
              </label>
              <div className="relative group">
                <div className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#0f7699] transition-colors">
                  <FileKey size={18} />
                </div>
                <input
                  id="currentPassword"
                  type={showCurrentPassword ? "text" : "password"}
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleChange}
                  required
                  className="w-full h-12 sm:h-14 pl-12 sm:pl-14 pr-12 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-2xl text-sm sm:text-[15px] font-bold text-slate-700 dark:text-white focus:outline-none focus:ring-4 focus:ring-blue-500/10 dark:focus:ring-[#4fc3f7]/10 focus:border-[#0f7699] dark:focus:border-[#4fc3f7] transition-all"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onMouseEnter={() => setShowCurrentPassword(true)}
                  onMouseLeave={() => setShowCurrentPassword(false)}
                  onTouchStart={() => setShowCurrentPassword(true)}
                  onTouchEnd={() => setShowCurrentPassword(false)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#0f7699] transition-colors focus:outline-none"
                >
                  {showCurrentPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="newPassword" className="text-[10px] font-black uppercase tracking-widest text-[#0f7699] dark:text-[#4fc3f7] opacity-70 ml-1">
                {t("profile.neues_passwort", "Neues Passwort")}
              </label>
              <div className="relative group">
                <div className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#0f7699] transition-colors">
                  <Lock size={18} />
                </div>
                <input
                  id="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  required
                  className="w-full h-12 sm:h-14 pl-12 sm:pl-14 pr-12 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-2xl text-sm sm:text-[15px] font-bold text-slate-700 dark:text-white focus:outline-none focus:ring-4 focus:ring-blue-500/10 dark:focus:ring-[#4fc3f7]/10 focus:border-[#0f7699] dark:focus:border-[#4fc3f7] transition-all"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onMouseEnter={() => setShowNewPassword(true)}
                  onMouseLeave={() => setShowNewPassword(false)}
                  onTouchStart={() => setShowNewPassword(true)}
                  onTouchEnd={() => setShowNewPassword(false)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#0f7699] transition-colors focus:outline-none"
                >
                  {showNewPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                </button>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-[10px] text-slate-400 dark:text-slate-500 ml-1 font-medium">
                  {t("profile.passwort_anforderung", "Mindestens 8 Zeichen, ein Großbuchstabe, ein Kleinbuchstabe und eine Zahl")}
                </p>
                {formData.newPassword && formData.currentPassword && formData.newPassword === formData.currentPassword && (
                  <p className="text-[11px] text-red-500 ml-1 font-bold flex items-center gap-1 animate-in slide-in-from-top-1">
                    <AlertCircle size={12} />
                    {t("profile.passwort_nicht_identisch", "Darf nicht identisch mit aktuellem Passwort sein")}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="text-[10px] font-black uppercase tracking-widest text-[#0f7699] dark:text-[#4fc3f7] opacity-70 ml-1">
                {t("profile.passwort_bestatigen", "Passwort bestätigen")}
              </label>
              <div className="relative group">
                <div className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#0f7699] transition-colors">
                  <ShieldCheck size={18} />
                </div>
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className="w-full h-12 sm:h-14 pl-12 sm:pl-14 pr-12 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-2xl text-sm sm:text-[15px] font-bold text-slate-700 dark:text-white focus:outline-none focus:ring-4 focus:ring-blue-500/10 dark:focus:ring-[#4fc3f7]/10 focus:border-[#0f7699] dark:focus:border-[#4fc3f7] transition-all"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onMouseEnter={() => setShowConfirmPassword(true)}
                  onMouseLeave={() => setShowConfirmPassword(false)}
                  onTouchStart={() => setShowConfirmPassword(true)}
                  onTouchEnd={() => setShowConfirmPassword(false)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#0f7699] transition-colors focus:outline-none"
                >
                  {showConfirmPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                </button>
              </div>

              {formData.confirmPassword && (
                 <div className={`mt-2 ml-1 flex items-center gap-2 text-xs font-bold animate-in slide-in-from-top-1 ${
                   formData.newPassword === formData.confirmPassword ? "text-emerald-600" : "text-red-500"
                 }`}>
                   {formData.newPassword === formData.confirmPassword ? (
                     <>
                       <CheckCircle2 size={14} />
                       <span>{t("profile.passwoerter_stimmen_uberein", "Passwörter stimmen überein")}</span>
                     </>
                   ) : (
                     <>
                       <AlertCircle size={14} />
                       <span>{t("profile.passwoerter_stimmen_nicht", "Passwörter stimmen nicht überein")}</span>
                     </>
                   )}
                 </div>
               )}
            </div>

            <div className="pt-6">
              <button
                type="submit"
                disabled={loading || (formData.newPassword && formData.currentPassword && formData.newPassword === formData.currentPassword) || (formData.newPassword && formData.confirmPassword && formData.newPassword !== formData.confirmPassword)}
                className="w-full h-12 sm:h-14 bg-[#0f7699] text-white rounded-2xl font-bold text-xs sm:text-sm tracking-widest shadow-xl shadow-blue-900/10 hover:bg-[#0d6482] transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <ShieldCheck size={18} />
                    <span>{t("profile.passwort_speichern", "PASSWORT SPEICHERN")}</span>
                  </>
                )}
              </button>
            </div>
          </form>
      </div>
    </div>
  );
}
